import SpinOverlay from 'app/components/UI-Elements/Feedback/Spin'
import { RouteType } from 'app/constants/GlobalTypes'
import { retry } from 'app/lib/utils/retry'
import * as React from 'react'
import { lazy, Suspense } from 'react'
import { Route, Routes as Switch } from 'react-router-dom'

import RestrictedRoute from './RestrictedRoute'

type PageType = { default: React.ComponentType<{ route: Partial<RouteType> }> }
type LazyPage = React.LazyExoticComponent<React.ComponentType<{ route: Partial<RouteType> }>>

// Route keys repeat across transactional and overlay pages, so cache by object.
const lazyPages = new WeakMap<RouteType, LazyPage>()

const getLazyPage = (route: RouteType): LazyPage => {
  const cached = lazyPages.get(route)
  if (cached) return cached

  const Page = lazy(() => retry(() => route.component) as Promise<PageType>)
  lazyPages.set(route, Page)
  return Page
}

const getRoutePath = (route: RouteType) => (route.exact ? route.path : `${route.path}/*`)

export const treeRenderer = (routes: RouteType[]) => {
  const renderRoute = (route: RouteType): React.ReactNode => {
    const Page = getLazyPage(route)

    return (
      <Route
        key={route.key}
        path={getRoutePath(route)}
        element={
          <RestrictedRoute restrictionMode={route.restrictionMode} allowFor={route.allowFor}>
            <Suspense fallback={<SpinOverlay loading={true} />}>
              <Page route={route} />
            </Suspense>
          </RestrictedRoute>
        }
      />
    )
  }

  const renderSubRoutes = (parent: RouteType) => {
    const Page = getLazyPage(parent)
    const feedbackRoutes = parent.routes.filter((route) => route.asFeedback)
    const routes = parent.routes.filter((route) => !route.asFeedback)

    return (
      <Route
        key={parent.key}
        path={getRoutePath(parent)}
        element={
          <RestrictedRoute allowFor={parent.allowFor}>
            <>
              <Switch>
                {routes.map((route) =>
                  route.routes.length > 0 ? renderSubRoutes(route) : renderRoute(route)
                )}
                <Route
                  key={parent.key}
                  path="/*"
                  element={
                    <Suspense fallback={<SpinOverlay loading={true} />}>
                      <Page route={parent} />
                    </Suspense>
                  }
                />
              </Switch>
              <Switch>{feedbackRoutes.map((route) => renderRoute(route))}</Switch>
            </>
          </RestrictedRoute>
        }
      />
    )
  }

  return routes.map((route) =>
    route.routes.length > 0 ? renderSubRoutes(route) : renderRoute(route)
  )
}
